import intersect from 'intersect';
import consolv from 'lib/consolv';
import queryString from 'query-string';

const defaultConfig = {
  enabled: false,
  timeout: 1000,
  failsafeTimeout: 2000,
  deals: false,
  includeTokens: true,
  revokeTokens: false,
  tokenConfig: {
    optOut: false,
    duration: 1209600,
    gdpr: {
      enabled: false,
    },
  },
};

function lookForTokens(a9Config) {
  const { apstag } = window;
  const defaults = defaultConfig.tokenConfig;
  const config = a9Config.tokenConfig || defaults;
  const queryStringParams = queryString.parse(window.location.search);
  const emailToken = queryStringParams.lvrmp;
  if (emailToken) {
    const tokenConfig = {
      hashedRecords: [
        { type: 'email', record: emailToken },
      ],
      gdpr: config.gdpr || defaults.gdpr,
      duration: config.duration || defaults.duration,
      optOut: config.optOut !== false ? config.optOut : defaults.optOut,
    };

    apstag.rpa(tokenConfig);
  }
}

function revokeTokens() {
  const { apstag } = window;
  apstag.dpa();
}

/* eslint-disable prefer-rest-params */
function setupA9Globals() {
  if (window.apstag) return;

  function queue(command, args) {
    window.apstag._Q.push([command, args]);
  }

  window.apstag = {
    init() {
      if (window.apstag._Q) {
        queue('i', arguments);
      } else {
        window.apstag.init.apply(null, arguments);
      }
    },
    fetchBids() {
      if (window.apstag._Q) {
        queue('f', arguments);
      } else {
        window.apstag.fetchBids.apply(null, arguments);
      }
    },
    setDisplayBids() {},
    targetingKeys() {
      return [];
    },
    dpa() {
      queue('di', arguments);
    },
    rpa() {
      queue('ri', arguments);
    },
    upa() {
      queue('ui', arguments);
    },
    _Q: [],
  };
}
/* eslint-enable prefer-rest-params */

/**
 * Creates the Amazon A9 request and callback that is called before loading slots
 */
const setupRequestBids = (MCP, config) => (event, { slots, addAsyncEvent }) => {
  const runPerformanceTest = MCP._config.performanceTest === true;
  let startTime;
  if (runPerformanceTest) startTime = window.performance.now();

  const asyncEvent = new Promise((resolve) => {
    const { apstag } = window;
    const { slots: slotMap = {}, failsafeTimeout } = config;
    let failsafeTimerRef;

    /* eslint-disable-next-line no-unused-vars */
    const adUnits = Array.from(slots).reduce((acc, [element, slot]) => {
      const slotType = slot.meta.bidAs || slot.type;
      const slotConfig = slotMap[slotType];
      const sizes = slot.sizeMap
        .filter(v => v !== 'fluid')
        .map((d => d.join(',')));
      if (!slotConfig) return acc;
      if (!sizes.length) return acc;

      // Will only include sizes listed in A9 configuration, unless no size map is configured
      const validSizes =
        Array.isArray(slotConfig.sizes) ?
          intersect(slotConfig.sizes, sizes) :
          sizes;

      if (validSizes.length) {
        acc.push({
          slotID: slot.id,
          slotName: slot.getAdUnitPath(),
          sizes: validSizes.map(v => v.split(',').map(n => Number(n))),
        });
      }
      return acc;
    }, []);

    if (!adUnits.length) {
      consolv.log('MCP: AmazonA9 has no valid ad units');
      resolve();
      return;
    }

    consolv.log('MCP: AmazonA9 bids ready', adUnits);

    /* eslint-disable-next-line prefer-const */
    failsafeTimerRef = window.setTimeout(() => {
      consolv.log(`MCP: AmazonA9 failsafe timeout exceeded at ${failsafeTimeout} seconds`);
      resolve();
    }, failsafeTimeout);

    apstag.fetchBids({ slots: adUnits },
      (bids) => {
        apstag.setDisplayBids();
        window.clearTimeout(failsafeTimerRef);

        if (runPerformanceTest) {
          const requestDuration = (window.performance.now() - startTime) / 1000;
          consolv.log('MCP: AmazonA9 requests received', bids, `${requestDuration} seconds`);
        } else {
          consolv.log('MCP: AmazonA9 requests received', bids);
        }

        resolve();
      });
  });

  addAsyncEvent(asyncEvent);
};

/**
 * Generates the configuration and sets up event listeners for slot loads
 * @param {MCPInstance} MCP
 */
export default function AmazonA9Adapter(MCP) {
  setupA9Globals();
  const { apstag } = window;

  MCP.loaded.then(() => {
    const { amazonA9 } =
      MCP.Configurator.getConfiguration({ defaults: { amazonA9: defaultConfig } });

    if (amazonA9.enabled) {
      apstag.init({
        pubID: amazonA9.pubID,
        adServer: 'googletag',
        bidTimeout: amazonA9.timeout,
        deals: amazonA9.deals,
      });

      if (amazonA9.includeTokens) lookForTokens(amazonA9);
      if (amazonA9.revokeTokens) revokeTokens();

      const requestBids = setupRequestBids(MCP, amazonA9);

      MCP.addEventListener('beforeRefreshSlots',
        (...params) => requestBids('beforeRefreshSlots', ...params));
    }
  });
}
